import React from 'react';
import { AlertCircle, RefreshCw, Home, ArrowLeft } from 'lucide-react';
import BrandLogo from '@/components/brand/BrandLogo';

export default class GlobalErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('GlobalErrorBoundary caught uncaught error:', error, errorInfo);
    this.setState({ errorInfo });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleHome = () => {
    window.location.href = '/';
  };

  handleApp = () => {
    window.location.href = '/app';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-cream-paper flex flex-col justify-center items-center px-4 py-12 text-forest-ink">
          <div className="max-w-md w-full text-center space-y-6">
            <div className="flex justify-center mb-2">
              <BrandLogo size="lg" />
            </div>

            <div className="w-14 h-14 mx-auto rounded-full bg-emerald-100/70 border border-emerald-200/80 flex items-center justify-center text-forest-ink">
              <AlertCircle className="w-7 h-7 text-forest-ink" />
            </div>

            <div className="space-y-2">
              <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-forest-ink">
                Something didn’t load quite right
              </h1>
              <p className="text-sm text-charcoal/80 leading-relaxed">
                Your medical notes, records, and subscription remain completely safe. You can reload the page or return to the companion app.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
              <button
                type="button"
                onClick={this.handleReload}
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-forest-ink text-white font-medium text-sm hover:bg-forest-shadow transition-colors shadow-sm"
              >
                <RefreshCw className="w-4 h-4" />
                Reload Page
              </button>
              <button
                type="button"
                onClick={this.handleApp}
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-white border border-border-mist text-forest-ink font-medium text-sm hover:bg-mist transition-colors shadow-2xs"
              >
                Go to Companion App
              </button>
              <button
                type="button"
                onClick={this.handleHome}
                className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full text-charcoal/80 hover:text-forest-ink font-medium text-sm transition-colors"
              >
                <Home className="w-4 h-4" />
                Home
              </button>
            </div>

            {process.env.NODE_ENV !== 'production' && this.state.error && (
              <details className="text-left bg-white border border-border-mist rounded-xl p-4 text-xs font-mono text-red-700 overflow-x-auto mt-6">
                <summary className="cursor-pointer font-sans font-semibold text-charcoal/80 mb-2">
                  Technical error details
                </summary>
                <p className="font-bold">{this.state.error.toString()}</p>
                <pre className="mt-2 whitespace-pre-wrap text-[11px] text-charcoal/70">
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}

            <p className="text-xs text-charcoal/60 pt-4">
              Need assistance? Email{' '}
              <a href="mailto:care@getbyonco.com" className="underline text-forest-ink font-medium">
                care@getbyonco.com
              </a>
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
